/*
  Flat mega menu — one custom element, one source of truth for "what's open".

  <mega-menu-flat> is rendered exactly once, by sections/header.liquid, and owns both
  surfaces:

    - The mobile drawer is a child of the element. It is rendered outside
      <sticky-header> because a position: fixed drawer nested inside the sticky header
      would be re-based by the header's scroll offsets.
    - The desktop triggers and panels live inside the header nav (absolutely positioned
      against .section-header, Dawn's only positioned header ancestor, so opening a
      panel never shifts the page). The element adopts them on connect via their
      data-* hooks.

  Because a single element holds both, opening the drawer closes any open panel and
  vice versa, Escape is handled in one place, and nothing depends on :hover.

  trapFocus / removeTrapFocus are globals from assets/global.js, which theme.liquid
  loads before this file.
*/
class MegaMenuFlat extends HTMLElement {
  constructor() {
    super();

    this.openPanelId = null;
    this.drawerIsOpen = false;

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onViewportResize = this.onViewportResize.bind(this);
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');

    // Desktop surface — lives in the header nav, outside this element.
    this.triggers = Array.from(document.querySelectorAll('[data-flat-trigger]'));
    this.panels = Array.from(document.querySelectorAll('[data-flat-panel]'));
    this.panelBackdrop = document.querySelector('[data-flat-backdrop]');

    // Mobile surface — inside this element, except the toggle, which has to stay in
    // the header's left grid slot.
    this.drawer = this.querySelector('[data-flat-drawer]');
    this.drawerBackdrop = this.querySelector('[data-flat-drawer-backdrop]');
    this.drawerCloseButton = this.querySelector('[data-flat-drawer-close]');
    this.drawerToggle = document.querySelector('[data-flat-drawer-toggle]');
    this.tabs = Array.from(this.querySelectorAll('[data-flat-tab]'));
    this.panes = Array.from(this.querySelectorAll('[data-flat-tab-pane]'));

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.togglePanel(trigger.dataset.flatTrigger));
    });

    this.drawerToggle?.addEventListener('click', () => {
      this.drawerIsOpen ? this.closeDrawer() : this.openDrawer();
    });
    this.drawerCloseButton?.addEventListener('click', () => this.closeDrawer());
    this.drawerBackdrop?.addEventListener('click', () => this.closeDrawer());

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab.dataset.flatTab));
      tab.addEventListener('keydown', this.onTabKeydown.bind(this));
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onViewportResize);
  }

  /* ------------------------------------------------------------------ desktop */

  togglePanel(panelId) {
    if (this.openPanelId === panelId) {
      this.closePanel();
    } else {
      this.openPanel(panelId);
    }
  }

  openPanel(panelId) {
    // Single source of truth: the two surfaces are mutually exclusive.
    this.closeDrawer({ returnFocus: false });

    this.openPanelId = panelId;

    this.panels.forEach((panel) => {
      panel.classList.toggle('mega-menu-flat__panel--open', panel.dataset.flatPanel === panelId);
    });
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.flatTrigger === panelId));
    });
    this.panelBackdrop?.classList.add('mega-menu-flat__backdrop--visible');

    document.addEventListener('keydown', this.onDocumentKeydown);
    document.addEventListener('click', this.onDocumentClick);
  }

  closePanel({ returnFocus = false } = {}) {
    if (!this.openPanelId) return;

    const activeTrigger = this.triggers.find((trigger) => trigger.dataset.flatTrigger === this.openPanelId);

    this.openPanelId = null;
    this.panels.forEach((panel) => panel.classList.remove('mega-menu-flat__panel--open'));
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    this.panelBackdrop?.classList.remove('mega-menu-flat__backdrop--visible');

    document.removeEventListener('click', this.onDocumentClick);
    this.releaseKeydownIfIdle();

    if (returnFocus) activeTrigger?.focus();
  }

  /*
    Closes the panel on any click that is neither inside a panel nor on a trigger —
    which covers the backdrop as well as the rest of the header (the logo, the cart
    icon), where the backdrop does not reach. Trigger clicks are ignored here so this
    listener does not immediately undo the click that opened the panel.
  */
  onDocumentClick(event) {
    if (event.target.closest('[data-flat-trigger]') || event.target.closest('[data-flat-panel]')) return;
    this.closePanel();
  }

  /* ------------------------------------------------------------------- mobile */

  openDrawer() {
    this.closePanel();

    this.drawerIsOpen = true;
    this.setViewportHeight();

    this.drawer?.classList.add('mega-menu-flat-drawer--open');
    this.drawerBackdrop?.classList.add('mega-menu-flat-drawer__backdrop--visible');
    this.drawerToggle?.setAttribute('aria-expanded', 'true');
    /*
      menu-open keeps the sticky header pinned, as Dawn's own drawer does.
      mega-menu-flat-open additionally lifts the header section's stacking order: it sets
      z-index: 3, which makes it a stacking context, so the drawer cannot out-stack the
      sibling announcement bar (z-index: 4) from inside it. Without this the bar paints
      over the top of the drawer and swallows clicks on the close button.
    */
    this.header?.classList.add('menu-open', 'mega-menu-flat-open');
    document.body.classList.add('overflow-hidden-tablet');

    document.addEventListener('keydown', this.onDocumentKeydown);
    window.addEventListener('resize', this.onViewportResize);

    if (this.drawer) trapFocus(this.drawer, this.drawerCloseButton || this.drawer);
  }

  closeDrawer({ returnFocus = true } = {}) {
    if (!this.drawerIsOpen) return;

    this.drawerIsOpen = false;

    this.drawer?.classList.remove('mega-menu-flat-drawer--open');
    this.drawerBackdrop?.classList.remove('mega-menu-flat-drawer__backdrop--visible');
    this.drawerToggle?.setAttribute('aria-expanded', 'false');
    this.header?.classList.remove('menu-open', 'mega-menu-flat-open');
    document.body.classList.remove('overflow-hidden-tablet');

    window.removeEventListener('resize', this.onViewportResize);
    this.releaseKeydownIfIdle();

    removeTrapFocus(returnFocus ? this.drawerToggle : null);
  }

  /* Swaps which pane is shown without touching drawer open/closed state. */
  selectTab(tabId) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.flatTab === tabId;
      tab.classList.toggle('mega-menu-flat-drawer__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panes.forEach((pane) => {
      pane.hidden = pane.dataset.flatTabPane !== tabId;
    });
  }

  onTabKeydown(event) {
    const key = event.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;

    event.preventDefault();

    const currentIndex = this.tabs.indexOf(event.currentTarget);
    const offset = key === 'ArrowRight' ? 1 : -1;
    const nextTab = this.tabs[(currentIndex + offset + this.tabs.length) % this.tabs.length];

    this.selectTab(nextTab.dataset.flatTab);
    nextTab.focus();
  }

  onViewportResize() {
    this.setViewportHeight();
  }

  setViewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }

  /* ------------------------------------------------------------------ shared */

  onDocumentKeydown(event) {
    if (event.key !== 'Escape') return;

    if (this.drawerIsOpen) {
      this.closeDrawer();
    } else {
      this.closePanel({ returnFocus: true });
    }
  }

  /* The keydown listener is shared by both surfaces, so only drop it once neither is open. */
  releaseKeydownIfIdle() {
    if (this.openPanelId || this.drawerIsOpen) return;
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }
}

customElements.define('mega-menu-flat', MegaMenuFlat);
